/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { Box, Grid, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLazyGetContractualDocumentLoanApplicationQuery } from "@api/documents";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { NavBarOcularVisit } from "@components/NavBarOcularVisit";
import { InactivityHandler } from "@components/InactivityHandler";
import { DocumentContract, ApiError } from "@interfaces/response";
import { LoanApplication } from "@interfaces/store";
import { CheckListSignatureDocument } from "@interfaces/catalogs";
import MifielScript from "@components/Firma/MifielScript";
import LegalarioScript from "@components/Firma/LegalarioScript";
import FirmaMiFiel from "@components/Firma/FirmaMiFiel";
import FirmaLegalario from "@components/Firma/FirmaLegalario";
import { SIGNATURE_TYPE } from "@helpers/constantes";
import { setModalErrorVisible } from "@store/slices/appSlice";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetLoanApplicationByIdQuery } from "@api/applications";
import IconRejected from "@components/Images/IconRejected";
import * as Sentry from "@sentry/react";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { useLazyGetDocumentosFirmaQuery } from "@api/configuration";
import useInterval from "@hooks/useInterval";
import { STATUS_CATALOG } from "@helpers/constantes";
import { setLoanApplication, setLoanStatus } from "@store/slices/registerSlice";
import { useNavigate } from "react-router-dom";

const SECONDS_TO_FETCH = 120;

interface RenderSignatureViewProps {
  loanApplication: LoanApplication | null;
  documentsToSign: DocumentContract[];
  checklistDocuments: CheckListSignatureDocument[];
}

const RenderSignatureView: React.FC<RenderSignatureViewProps> = ({
  loanApplication,
  documentsToSign,
  checklistDocuments,
}) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  if (loanApplication) {
    if (loanApplication.signature_type === SIGNATURE_TYPE.digital) {
      if (interfaceConfig.firma?.provider === "legalario") {
        return <FirmaLegalario documents={documentsToSign} checklistDocuments={checklistDocuments} />;
      } else if (interfaceConfig.firma?.provider === "mifiel") {
        return <FirmaMiFiel documents={documentsToSign} />;
      }
    } else {
      <Box>
        <Typography
          mt={4}
          textAlign={"center"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.main}
          fontWeight={600}
        >
          Te encuentras en el ultimo paso para obtener tu crédito, a continuación un asesor se pondrá en contacto
          contigo para coordinar la firma del contrato
        </Typography>

        <Box display="flex" justifyContent="center" mt={4}>
          <IconRejected width={isMobileOrTablet ? "150" : "250"} fill={theme.palette.primary.light} />
        </Box>

        <Typography
          mt={4}
          textAlign={"center"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.dark}
        >
          ¡Despreocúpate!
        </Typography>

        <Typography
          mt={1}
          textAlign={"center"}
          variant={isMobileOrTablet ? "body2" : "body1"}
          color={theme?.palette?.primary?.dark}
        >
          Puedes salir de esta pantalla, te contactaremos.
        </Typography>
      </Box>;
    }
  }

  return (
    <Box mt={4} display="flex" flexDirection="column" alignItems="center">
      <Skeleton variant="rectangular" sx={{ height: "190px", width: "100%", borderRadius: 2 }} />
    </Box>
  );
};

const FirmaDocumentos: React.FC = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const navigate = useNavigate();

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);

  const [getLoanApplicationById] = useLazyGetLoanApplicationByIdQuery();
  const [getDocumentByApplicationClient] = useLazyGetContractualDocumentLoanApplicationQuery();
  const [getChecklistDocuments] = useLazyGetDocumentosFirmaQuery();

  const [loanApplicationState, setLoanApplicationState] = useState<LoanApplication | null>(null);
  const [documentsToSign, setDocumentsToSign] = useState<DocumentContract[]>([]);
  const [checklistDocuments, setChecklistDocuments] = useState<CheckListSignatureDocument[]>([]);

  useEffect(() => {
    (async () => {
      try {
        if (!application.id || !clientDetails.id) {
          throw new Error("Application ID or client ID is not defined");
        }

        const signature_config = interfaceConfig.firma;
        if (!signature_config) {
          throw new Error("Signature config is not defined");
        }

        if (!signature_config.provider) {
          throw new Error("Signature provider is not defined");
        }

        if (!application.tax_system_id) {
          throw new Error("Tax system ID is not defined");
        }

        const [loanApplication, documents, checklist_documents] = await Promise.all([
          getLoanApplicationById(application.id).unwrap(),
          getDocumentByApplicationClient({
            loanApplicationId: application.id,
            clientId: clientDetails.id,
          }).unwrap(),
          getChecklistDocuments({
            tax_system_id: application.tax_system_id,
          }).unwrap(),
        ]);

        setLoanApplicationState(loanApplication);
        setDocumentsToSign(documents);
        setChecklistDocuments(checklist_documents);
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            component: "FirmaDocumentos-loading",
            values: {
              applicationId: application.id,
              clientId: clientDetails.id,
            },
          },
        });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      }
    })();
  }, [application.id, clientDetails.id]);

  const loanInterval = useInterval(async () => {
    try {
      const { firma_representante_legal } = STATUS_CATALOG;
      const loan_application = await getLoanApplicationById(application.id).unwrap();
      if (loan_application?.status_catalog && loan_application.status_catalog.code !== firma_representante_legal.code) {
        dispatch(setLoanApplication(loan_application));
        dispatch(setLoanStatus(loan_application.status_catalog.code));
        navigate(loan_application.status_catalog.url_actual);
      }
    } catch (error) {
      console.error("Ha ocurrido un error al obtener la aplicación");
    }
  }, SECONDS_TO_FETCH);

  useEffect(() => {
    loanInterval.setIsActiveInterval(true);

    return () => {
      loanInterval.setIsActiveInterval(false);
    };
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        <InactivityHandler />
        <MifielScript />
        <LegalarioScript />

        <Box mx={4}>
          <Typography
            mt={5}
            variant={isMobileOrTablet ? "h5" : "h4"}
            fontWeight={700}
            color={theme?.palette?.primary?.dark}
            textAlign="center"
          >
            Firma de documentos
          </Typography>

          <NavBarOcularVisit currentStep={4} />

          <RenderSignatureView
            loanApplication={loanApplicationState}
            documentsToSign={documentsToSign}
            checklistDocuments={checklistDocuments}
          />
        </Box>
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default FirmaDocumentos;
